import {
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';

import type { FacetGroup } from '@src/facets';
import { ACTION_FIELD_OPTIONS, generateId, isFamilyField, isJsonField, type CatalogRuleAction, type KeyValuePair } from './types';
import FacetAutocomplete from './FacetAutocomplete';

interface ActionRowProps {
  action: CatalogRuleAction;
  facetGroups: FacetGroup[];
  onChange: (id: string, updates: Partial<CatalogRuleAction>) => void;
  onDelete: (id: string) => void;
}

const ActionRow = ({ action, facetGroups, onChange, onDelete }: ActionRowProps) => {
  const jsonMode = isJsonField(action.field);
  const familyMode = isFamilyField(action.field);

  const handleFieldChange = (field: string) => {
    const wasJson = isJsonField(action.field);
    const nowJson = isJsonField(field);

    onChange(action.id, {
      field,
      ...(nowJson && !wasJson ? { value: '', pairs: [{ id: generateId(), key: '', value: '', values: [] }] } : {}),
      ...(wasJson && !nowJson ? { value: '', pairs: [] } : {}),
    });
  };

  const handlePairChange = (pairId: string, updates: Partial<KeyValuePair>) => {
    onChange(action.id, {
      pairs: action.pairs.map(p =>
        p.id === pairId ? { ...p, ...updates } : p,),
    });
  };

  const handleAddPair = () => {
    onChange(action.id, {
      pairs: [...action.pairs, { id: generateId(), key: '', value: '', values: [] }],
    });
  };

  const handleRemovePair = (pairId: string) => {
    onChange(action.id, {
      pairs: action.pairs.filter(p => p.id !== pairId),
    });
  };

  const getAvailableTagChoices = (currentPairId: string): string[] => {
    const usedTags = new Set(
      action.pairs.filter(p => p.id !== currentPairId).map(p => p.key).filter(Boolean),
    );
    return facetGroups.map(fg => fg.tag).filter(tag => !usedTags.has(tag));
  };

  const getValueChoices = (tag: string): string[] => {
    const facetGroup = facetGroups.find(fg => fg.tag === tag);
    return facetGroup?.values.map(v => v.name) ?? [];
  };

  return (
    <Box mb={1.5}>
      <Box display='flex' alignItems='flex-start' gap={1.5}>
        <Typography variant='body2' fontWeight={600} sx={{ minWidth: 80, mt: 1, color: 'text.primary', whiteSpace: 'nowrap' }}>
          THEN SET
        </Typography>

        <TextField
          select
          size='small'
          value={action.field}
          onChange={e => handleFieldChange(e.target.value)}
          sx={{ width: 180, flexShrink: 0 }}
          SelectProps={{ displayEmpty: true }}
          InputProps={{ sx: { bgcolor: 'background.paper' } }}
        >
          <MenuItem value='' disabled>Select a Field</MenuItem>
          {ACTION_FIELD_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>

        <Typography variant='body1' sx={{ mt: 1, color: 'text.secondary' }}>
          to
        </Typography>

        {/* Standard value input for non-JSON fields */}
        {!jsonMode && (
          <TextField
            size='small'
            placeholder='Value'
            value={action.value}
            onChange={e => onChange(action.id, { value: e.target.value })}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ sx: { bgcolor: 'background.paper' } }}
          />
        )}

        {/* Family field: facet tag + multi-select values */}
        {familyMode && (
          <Box sx={{ flex: 1 }}>
            {action.pairs.map((pair, i) => (
              <Box key={pair.id} display='flex' alignItems='flex-start' gap={1} mt={i > 0 ? 1 : 0}>
                <FacetAutocomplete
                  options={getAvailableTagChoices(pair.id)}
                  value={pair.key || null}
                  onChange={value => handlePairChange(pair.id, { key: value, values: [] })}
                  placeholder='Facet Tag'
                  matchLabel='Tag Matches'
                  sx={{ minWidth: 150 }}
                />
                <FacetAutocomplete
                  multiple
                  options={getValueChoices(pair.key)}
                  value={pair.values}
                  onChange={values => handlePairChange(pair.id, { values })}
                  placeholder={pair.values.length === 0 ? 'Type or select values' : ''}
                  matchLabel='Value Matches'
                  sx={{ flex: 1, minWidth: 200 }}
                />
                <IconButton size='small' onClick={() => handleRemovePair(pair.id)} sx={{ mt: 0.5, color: 'text.secondary' }}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            ))}
            <Button
              size='small'
              startIcon={<AddIcon />}
              onClick={handleAddPair}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add Facet
            </Button>
          </Box>
        )}

        {/* Other JSON fields: plain key/value text inputs */}
        {jsonMode && !familyMode && (
          <Box sx={{ flex: 1 }}>
            {action.pairs.map((pair, i) => (
              <Box key={pair.id} display='flex' alignItems='center' gap={1} mt={i > 0 ? 1 : 0}>
                <TextField
                  size='small'
                  placeholder='Key'
                  value={pair.key}
                  onChange={e => handlePairChange(pair.id, { key: e.target.value })}
                  sx={{ minWidth: 100 }}
                  InputProps={{ sx: { bgcolor: 'background.paper' } }}
                />
                <TextField
                  size='small'
                  placeholder='Value'
                  value={pair.value}
                  onChange={e => handlePairChange(pair.id, { value: e.target.value })}
                  sx={{ minWidth: 120 }}
                  InputProps={{ sx: { bgcolor: 'background.paper' } }}
                />
                <IconButton size='small' onClick={() => handleRemovePair(pair.id)} sx={{ color: 'text.secondary' }}>
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
            ))}
            <Button
              size='small'
              startIcon={<AddIcon />}
              onClick={handleAddPair}
              sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
            >
              Add Pair
            </Button>
          </Box>
        )}

        <IconButton size='small' onClick={() => onDelete(action.id)} sx={{ mt: 0.25, color: 'text.secondary' }}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ActionRow;
